/**
 * @fileoverview Entity Picker main interface
 */
// @ts-nocheck
import {UrlManipulator} from '/js/yext/ui/urlmanipulator/urlmanipulator';
import {cartLabel} from '/ui/components/entitypicker/cart/cartlabel';
import {CartModel} from '/ui/components/entitypicker/cart/cartmodel';
import {selectionModelToUrlObject, cartModelToSelectionModel}
  from '/ui/components/entitypicker/cart/modelhelpers';
import {EntityPickerContainer} from '/ui/components/entitypicker/container/entitypickercontainer';
import {ItemTypes} from '/ui/components/entitypicker/container/itemtypes';
import {AjaxDataSource} from '/ui/components/entitypicker/datasource/ajaxsource';
import {DataModel} from '/ui/components/entitypicker/datasource/datamodel';
import {DataSource, SerializableProto} from '/ui/components/entitypicker/datasource/datasource';
import * as toast from '/ui/components/toast/toast';
import {yext} from '/ui/lib/msg';
import {useKeyDown} from '/ui/lib/usekeydown';

const QUERY_PARAM = 'uep';
const SEARCH_DELAY = 300; // Delay between keypress and search

function EscapeHack({onEscape}) {
  useKeyDown('Escape', onEscape);
  return null;
}

/**
 * @typedef {{
 *   data: !DataModel,
 *   isLoading: boolean,
 *   onShowMore: function(string): void,
 *   onSearch: function(string): void,
 *   onSelect: function(!CartModel): void,
 * }}
 */
export let SingleSelectContainerProps;


/**
 * @typedef
 * function(SingleSelectContainerProps): React.ReactElement
 */
export let SingleSelectContainerComponent;

/**
 * @typedef {{
 *   alignMenu: (string|undefined),
 *   allowContainerOverflow: (boolean|undefined),
 *   chromeless: (boolean|undefined),
 *   disabled: (boolean|undefined),
 *   dropdownExpanded: ?,
 *   label: string,
 *   onCloseDropdown: function(): ?,
 *   onToggleDropdown: function(): ?,
 *   trigger: (?|undefined),
 *   wrapperClassName: (?|undefined),
 *   children: (React.ReactNode|undefined)
 * }}
 */
export let TriggerProps;

/**
 * @typedef
 * function(TriggerProps): React.ReactElement
 */
let TriggerComponent;

/**
 * @typedef {{
 *   additionalFeatures: ?,
 *   additionalSearchFields: ?,
 *   allowEntityTypeSelection: ?,
 *   allowedItems: ?,
 *   allowedSelectAllItems: ?,
 *   alternatePermissions: ?,
 *   applyInitialSelection: ?,
 *   asFilter: ?,
 *   dataSourceUrl: ?,
 *   activationFilterType: ?,
 *   entityTypes: ?,
 *   entityIds: ?,
 *   savedSearchIds?: !Array<number>,
 *   compoundSearchFilter?: !SerializableProto,
 *   includeToggler: ?,
 *   includeArchivedEntities: boolean,
 *   initialSelection: ?,
 *   locationsOnly: ?,
 *   mock: {
 *     dataSource: (DataSource|undefined),
 *   },
 *   multiCategory: ?,
 *   onCancel: ?,
 *   onSelect: ?,
 *   persistInUrl: ?,
 *   rootRefCallback: ?,
 *   selection: ?,
 *   singleSelect: ?,
 *   sortOrderId: ?,
 *   wrapperClassName?: ?,
 *   fullStoryId?: ?,
 *   trigger?: ?,
 *   allowContainerOverflow?: boolean,
 *   advancedEntityGrouping?: boolean,
 *   chromeless?: boolean,
 *   skipSetStateOnMount?: boolean,
 *   alignMenu?: string,
 *   disabled?: boolean,
 *   handleEntityAddClick?: ?function(),
 *   }}
 */

export let EntityPickerControllerProps;

/**
 * @typedef {{
 *   additionalFeatures: ?,
 *   additionalSearchFields: ?,
 *   allowEntityTypeSelection: ?,
 *   allowedItems: ?,
 *   allowedSelectAllItems: ?,
 *   alternatePermissions: ?,
 *   applyInitialSelection: ?,
 *   asFilter: ?,
 *   dataSourceUrl: ?,
 *   activationFilterType: ?,
 *   entityTypes: ?,
 *   entityIds: ?,
 *   savedSearchIds?: !Array<number>,
 *   compoundSearchFilter?: ?,
 *   includeToggler: ?,
 *   includeArchivedEntities: boolean,
 *   initialSelection: ?,
 *   locationsOnly: ?,
 *   mock: {
 *     dataSource: (DataSource|undefined),
 *   },
 *   multiCategory: ?,
 *   onCancel: ?,
 *   onSelect: ?,
 *   persistInUrl: ?,
 *   rootRefCallback: ?,
 *   selection: ?,
 *   singleSelect: ?,
 *   sortOrderId: ?,
 *   wrapperClassName?: ?,
 *   fullStoryId?: ?,
 *   trigger?: ?,
 *   allowContainerOverflow?: boolean,
 *   advancedEntityGrouping?: boolean,
 *   chromeless?: boolean,
 *   skipSetStateOnMount?: boolean,
 *   alignMenu?: string,
 *   disabled?: boolean,
 *   handleEntityAddClick?: ?function(),
 *   DefaultTrigger: TriggerComponent,
 *   SingleSelectContainer: SingleSelectContainerComponent,
 * }}
 */
export let EntityPickerControllerCoreProps;

export class EntityPickerControllerCore extends React.Component {
  constructor(props) {
    super(props);

    /** @type {EntityPickerControllerCoreProps} */
    this.props;
    this.selectHandler = this.selectHandler.bind(this);
    this.searchHandler = this.searchHandler.bind(this);
    this.showMoreHandler = this.showMoreHandler.bind(this);
    this.controllerRootRefCallback = this.controllerRootRefCallback.bind(this);

    /** @type {?number} */
    this.searchTimer = null;

    /** @type {DataSource} */
    this.dataSource = this.props.mock.dataSource
      ? this.props.mock.dataSource
      : new AjaxDataSource(
        `/s/${window['businessId']}${this.props.dataSourceUrl}`,
        this.props.additionalSearchFields,
        this.props.alternatePermissions,
        this.props.activationFilterType,
        this.props.additionalFeatures,
        this.props.sortOrderId,
        this.props.includeArchivedEntities,
        this.props.advancedEntityGrouping);

    this.url = UrlManipulator.fromCurrent();
    this.usingEntities = this.props.allowedItems.includes(ItemTypes.ENTITIES);

    this.state = {
      dropdownExpanded: false,
      isLoading: true,
      data: new DataModel(),
      selectedItems: new CartModel(this.usingEntities),
    };

    this.DefaultTrigger = this.props.DefaultTrigger;
    this.SingleSelectContainer = this.props.SingleSelectContainer;
  }

  componentDidMount() {
    const initialSelection =
      this.props.selection
      || (this.props.persistInUrl && this.url.getQueryParam(QUERY_PARAM))
      || this.props.initialSelection;
    if (!isInitialSelectionEmpty(initialSelection) && !this.props.skipSetStateOnMount) {
      this.setStateFromSelection(initialSelection, this.props.applyInitialSelection);
    }

    this.dataSource.searchWithTerm('', this.props.entityTypes, this.props.entityIds, this.props.savedSearchIds, this.props.compoundSearchFilter)
      .then(data => this.setState({data, isLoading: false}))
      .catch(handleDataSourceError);
  }

  componentDidUpdate(prevProps, prevState) {
    if (!areSelectionsEqual(this.props.selection, prevProps.selection)) {
      this.setStateFromSelection(this.props.selection, false, prevProps.selection);
    }

    // Relevant props have been updated as to warrant a call to re-fetch entities.
    const shouldRefetchEntities =
      this.props.entityIds !== prevProps.entityIds
      || this.props.entityTypes !== prevProps.entityTypes
      || this.props.savedSearchIds !== prevProps.savedSearchIds
      || this.props.compoundSearchFilter !== prevProps.compoundSearchFilter;

    if (shouldRefetchEntities) {
      this.dataSource.searchWithTerm('', this.props.entityTypes, this.props.entityIds, this.props.savedSearchIds, this.props.compoundSearchFilter)
        .then(data => this.setState({data, isLoading: false}))
        .catch(handleDataSourceError);
    }
  }

  componentWillUnmount() {
    this.dataSource.cancel();
  }

  /** @suppress {checkTypes} */
  setStateFromSelection(selection, apply, prevSelection = null) {
    const callback = apply
      ? () => this.selectHandler(this.state.selectedItems)
      : () => {};

    if (!selectionHasIds(selection)) {
      this.setState(
        {selectedItems: CartModel.fromUrlObject({}, selection, this.usingEntities)},
        callback);
    } else if (prevSelection && isSelectionASubset(prevSelection, selection)) { // new selection is a subset of the old selection (in which case we don't need an ajax call)
      this.setState(state => ({
        selectedItems: state.selectedItems.subsetFromSelection(selection),
      }), callback);
    } else {
      const entityTypes = selection.entityTypes;
      const entities = selection.entities;
      const folders = selection.folders;
      const labels = selection.labels;
      // TODO(amullings): Validation?
      // TODO(amullings): Prevent selecting multiple item types?
      this.dataSource.searchWithIds(entityTypes, entities, folders, labels)
        .then(data => {
          const selectedItems = CartModel.fromUrlObject(data, selection, this.usingEntities);
          this.setState({selectedItems}, callback);
        })
        .catch(handleDataSourceError);
    }
  }

  controllerRootRefCallback(rootElement) {
    rootElement.addEventListener(
      'uep-apply-selection',
      e => {
        const newSelection = e.detail.selection;
        this.setStateFromSelection(newSelection, e.detail.apply);
      },
    );

    this.props.rootRefCallback(rootElement);
  }

  /**
   * @param {!CartModel} items
   */
  selectHandler(items) {
    const selectionOptions = {multiCategory: this.props.multiCategory};
    const selection = cartModelToSelectionModel(items, selectionOptions);
    if (this.props.persistInUrl) {
      this.url.setQueryParam(QUERY_PARAM, selectionModelToUrlObject(selection));
    }
    this.setState({
      dropdownExpanded: false,
      selectedItems: items,
    });

    this.props.onSelect(selection);
  }

  searchHandler(term) {
    // Determine if this picker is only for entity types
    const searchOnlyEntityTypes =
      this.props.allowedItems.length === 1 && this.props.allowedItems[0] === ItemTypes.ENTITYTYPES;
    // Term to send to data source, need all results if picker is for entity types
    const dataSourceTerm = searchOnlyEntityTypes ? '' : term;

    clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(
      () => {
        this.setState({isLoading: true});
        this.dataSource.searchWithTerm(dataSourceTerm, this.props.entityTypes, this.props.entityIds, this.props.savedSearchIds, this.props.compoundSearchFilter)
          .then(retrievedData => {
            if (!searchOnlyEntityTypes) {
              this.setState({data: retrievedData, isLoading: false});
            } else {
              this.setState({
                data: retrievedData.filterEntityTypes(t =>
                  t.label.toLocaleLowerCase().includes(term.toLocaleLowerCase())),
                isLoading: false,
              });
            }
          })
          .catch(handleDataSourceError);
      },
      SEARCH_DELAY);
  }

  showMoreHandler(query) {
    this.setState({isLoading: true});
    this.dataSource.searchWithQuery(query)
      .then(data => this.setState(state => ({
        data: state.data.mergeIn(data),
        isLoading: false,
      })))
      .catch(handleDataSourceError);
  }

  render() {
    const toggleDropdown = () => this.setState(state => ({
      dropdownExpanded: !state.dropdownExpanded,
    }));
    const closeDropdown = () => this.setState({dropdownExpanded: false});
    const cancel = () => {
      this.setState({dropdownExpanded: false});
      this.props.onCancel();
    };

    const picker = this.props.advancedEntityGrouping
      ? <this.SingleSelectContainer
        data={this.state.data}
        isLoading={this.state.isLoading}
        onShowMore={this.showMoreHandler}
        onSearch={this.searchHandler}
        onSelect={this.selectHandler}
      />
      : <EntityPickerContainer
        {...this.props}
        initialCart={this.state.selectedItems}
        data={this.state.data}
        isLoading={this.state.isLoading}
        searcher={this.searchHandler}
        onShowMore={this.showMoreHandler}
        onSelect={this.selectHandler}
        onCancel={cancel}
        rootRefCallback={this.controllerRootRefCallback}
        dropdownExpanded={this.state.dropdownExpanded}
      />;

    return this.props.includeToggler
      ? <this.DefaultTrigger
        dropdownExpanded={this.state.dropdownExpanded}
        label={cartLabel(this.state.selectedItems, this.props)}
        onToggleDropdown={toggleDropdown}
        onCloseDropdown={closeDropdown}
        wrapperClassName={this.props.wrapperClassName}
        chromeless={this.props.chromeless}
        trigger={this.props.trigger}
        allowContainerOverflow={this.props.allowContainerOverflow}
        alignMenu={this.props.alignMenu}
        disabled={this.props.disabled}
      >

        <EscapeHack onEscape={closeDropdown} />
        {picker}
      </this.DefaultTrigger>
      : picker;
  }
}

EntityPickerControllerCore['defaultProps'] = {
  mock: {},
  locationsOnly: false,
  includeToggler: true,
  asFilter: true,
  allowEntityTypeSelection: true,
  allowedItems: [ItemTypes.ENTITIES, ItemTypes.FOLDERS, ItemTypes.LABELS],
  allowedSelectAllItems: [ItemTypes.ENTITIES, ItemTypes.FOLDERS, ItemTypes.LABELS],
  singleSelect: false,
  multiCategory: false,
  dataSourceUrl: '/components/entitypicker/search',
  additionalSearchFields: [],
  alternatePermissions: [],
  additionalFeatures: [],
  persistInUrl: false,
  entityTypes: [],
  entityIds: [],
  activationFilterType: [],
  initialSelection: {},
  applyInitialSelection: false,
  sortOrderId: 'location.business_name',
  includeArchivedEntities: false,
  onSelect: () => {
    throw new Error('onSelect not provided to EntityPickerController');
  },
  onCancel: () => {},
  rootRefCallback: () => {},
};

function isInitialSelectionEmpty(selection) {
  const allSelectedSet = selection.allSelected
    ? validateItemType(selection.allSelected) || selection.allSelected.length : false;

  return !selectionHasIds(selection) && !allSelectedSet;
}

function handleDataSourceError(aborted) {
  if (!aborted) {
    showError();
  }
}

function showError() {
  toast.showErrorToast(
    <span>
      {yext.msg('Something went wrong. Please refresh the page and try again.')}
    </span>,
  );
}

/**
 * @param {!Object} selection1 selection object to check
 * @param {!Object} selection2 selection object to check
 * @return {boolean} whether the two selections are equal (deep equality)
 */
function areSelectionsEqual(selection1, selection2) {
  if (!!selection1 !== !!selection2) {
    return false;
  }
  if (!selection1) {
    return true;
  }

  const fieldsToCompare = [ // Can't use string keys because of closure
    [[selection1.allSelected].flat(), [selection2.allSelected].flat()],
    [selection1.entityTypes, selection2.entityTypes],
    [selection1.entities, selection2.entities],
    [selection1.folders, selection2.folders],
    [selection1.labels, selection2.labels],
  ];
  return fieldsToCompare.every(([field1, field2]) => {
    if (isFieldEmpty(field1) !== isFieldEmpty(field2)) {
      return false;
    }
    if (isFieldEmpty(field1)) {
      return true;
    }
    if (field1.length !== field2.length) {
      return false;
    }
    return field1.every(fieldElem => field2.includes(fieldElem));
  });
}

function isFieldEmpty(field) {
  return !field || field.length === 0;
}

/**
 * @param {?Object} selection selection object to check
 * @return {boolean} whether the selection contains ids or not
 */
function selectionHasIds(selection) {
  if (!selection) {
    return false;
  }

  const selections = [
    selection.entityTypes,
    selection.entities,
    selection.folders,
    selection.labels,
  ];

  return selections.some(a => Array.isArray(a) && a.length > 0);
}

/**
 * checks if newSelection is a subset (improper or proper) of oldSelection
 *
 * @param {!Object} oldSelection
 * @param {!Object} newSelection
 * @return {boolean} true iff newSelection is a subset of or equal to oldSelection
 */
function isSelectionASubset(oldSelection, newSelection) {
  const fieldsToCompare = [ // Can't use string keys because of closure
    [oldSelection.entityTypes, newSelection.entityTypes],
    [oldSelection.entities, newSelection.entities],
    [oldSelection.folders, newSelection.folders],
    [oldSelection.labels, newSelection.labels],
  ];
  return fieldsToCompare.every(fields => isSubset(fields[0], fields[1]));
}

function isSubset(oldVals = [], newVals = []) {
  return newVals.every(currVal => oldVals.includes(currVal));
}

/**
 * @param {?} type something that could be an itemType symbol
 * @return {?Symbol} the validated symbol or null if param is not an itemType
 */
function validateItemType(type) {
  return Object.values(ItemTypes).includes(type) ? type : null;
}
